import React, { useEffect, useRef, useState } from 'react'
import { Loader } from 'semantic-ui-react'

import './styles.css'

interface ValidationStatusIconProps {
  isLoading: boolean
  isError: boolean
  /**
   * When true, render `null` until `isLoading` or `isError` change at least
   * once after mount. Use to suppress the "all-clear" flash that would
   * otherwise appear on every page load. Defaults to `false` — callers that
   * own their own gating (e.g. via a parent-owned `showUpdateStatus` flag)
   * should leave this as `false` and toggle it themselves.
   */
  suppressUntilFirstChange?: boolean
}

/**
 * Renders a 3-state validation indicator next to a setting / form field:
 *
 * - `isLoading=true` → SUI `<Loader inline size="tiny">`.
 * - `isError=true` (and not loading) → red `fa-times-circle`.
 * - Otherwise → green `fa-check`.
 *
 * Consolidates two near-identical hand-rolled `UpdateStatusIcon` helpers
 * (PET-1912). Layout utilities (`flex`, `items-center`, `mx-2`,
 * `self-center`, `marginRight: 10`) move from inline class chains into the
 * co-located `styles.css`; the Font Awesome glyph + `text-red` / `text-green`
 * colour tokens stay inline because they are the icon's intrinsic identity.
 */
export default function ValidationStatusIcon({
  isLoading,
  isError,
  suppressUntilFirstChange = false,
}: ValidationStatusIconProps) {
  const [hasPropsChanged, setHasPropsChanged] = useState(false)
  const isFirstEffectRef = useRef(true)

  useEffect(() => {
    if (isFirstEffectRef.current) {
      isFirstEffectRef.current = false
      return
    }
    setHasPropsChanged(true)
  }, [isLoading, isError])

  if (suppressUntilFirstChange && !hasPropsChanged) {
    return null
  }

  if (isLoading) {
    return (
      <Loader active inline size="tiny" className="ss-validation-status-icon__loader" />
    )
  }

  if (isError) {
    return (
      <i
        aria-label="error"
        className="fal fa-circle-xmark text-red ss-validation-status-icon ss-validation-status-icon--error"
      />
    )
  }

  return (
    <i
      aria-label="success"
      className="fas fa-check text-green ss-validation-status-icon ss-validation-status-icon--success"
    />
  )
}



// WEBPACK FOOTER //
// ./src/shared/components/ValidationStatusIcon/index.tsx